import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaCalendar, FaUser, FaArrowRight } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';

const BlogPage = () => {
  const [posts, setPosts] = useState([
    {
      id: 1,
      title: 'The Ultimate Guide to Collecting Rare Coins',
      excerpt: 'Discover the essential tips and strategies for building a valuable coin collection. Learn about grading, authentication, and market trends.',
      author: 'John Smith',
      date: '2024-01-15',
      category: 'Coins',
      image: '/images/blog/coins-guide.jpg',
      featured: true
    },
    {
      id: 2,
      title: 'Investing in Precious Metals: Gold vs Silver',
      excerpt: 'Compare the investment potential of gold and silver. Understand market dynamics, storage options, and long-term value.',
      author: 'Sarah Johnson',
      date: '2024-01-10',
      category: 'Investment',
      image: '/images/blog/gold-silver.jpg',
      featured: false
    },
    {
      id: 3,
      title: 'How to Identify Authentic Gemstones',
      excerpt: 'Learn professional techniques for verifying gemstone authenticity. Avoid counterfeits and make informed purchases.',
      author: 'Michael Chen',
      date: '2024-01-05',
      category: 'Gems',
      image: '/images/blog/gemstones.jpg',
      featured: false
    },
    {
      id: 4,
      title: 'Top 10 Most Valuable Collectibles of 2024',
      excerpt: 'Explore the most sought-after collectibles this year. Market insights and investment opportunities.',
      author: 'Emily Davis',
      date: '2024-01-01',
      category: 'Collectibles',
      image: '/images/blog/collectibles-2024.jpg',
      featured: false
    }
  ]);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const categories = ['all', 'Coins', 'Investment', 'Gems', 'Collectibles'];

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <PageContainer>
      <PageHeader>
        <Container>
          <SectionTitle 
            title="Collector's Blog" 
            subtitle="Expert insights, guides, and news from the world of collectibles"
          />
        </Container>
      </PageHeader>

      <Container>
        <CategoryFilter>
          {categories.map(category => (
            <CategoryButton
              key={category}
              $active={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </CategoryButton>
          ))}
        </CategoryFilter>

        {filteredPosts.length > 0 && filteredPosts[0].featured && (
          <FeaturedPost>
            <FeaturedImage style={{ backgroundImage: `url(${filteredPosts[0].image})` }}>
              <FeaturedBadge>Featured</FeaturedBadge>
            </FeaturedImage>
            <FeaturedContent>
              <PostCategory>{filteredPosts[0].category}</PostCategory>
              <FeaturedTitle>{filteredPosts[0].title}</FeaturedTitle>
              <FeaturedExcerpt>{filteredPosts[0].excerpt}</FeaturedExcerpt>
              <PostMeta>
                <MetaItem>
                  <FaUser /> {filteredPosts[0].author}
                </MetaItem>
                <MetaItem>
                  <FaCalendar /> {new Date(filteredPosts[0].date).toLocaleDateString()}
                </MetaItem>
              </PostMeta>
              <ReadMoreButton to={`/blog/${filteredPosts[0].id}`}>
                Read Full Article <FaArrowRight />
              </ReadMoreButton>
            </FeaturedContent>
          </FeaturedPost>
        )}

        <PostsGrid>
          {filteredPosts.slice(filteredPosts[0]?.featured ? 1 : 0).map(post => (
            <PostCard key={post.id}>
              <PostImage style={{ backgroundImage: `url(${post.image})` }} />
              <PostContent>
                <PostCategory>{post.category}</PostCategory>
                <PostTitle>{post.title}</PostTitle>
                <PostExcerpt>{post.excerpt}</PostExcerpt>
                <PostMeta>
                  <MetaItem>
                    <FaUser /> {post.author}
                  </MetaItem>
                  <MetaItem>
                    <FaCalendar /> {new Date(post.date).toLocaleDateString()}
                  </MetaItem>
                </PostMeta>
                <ReadMoreLink to={`/blog/${post.id}`}>
                  Read More <FaArrowRight />
                </ReadMoreLink>
              </PostContent>
            </PostCard>
          ))}
        </PostsGrid>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
`;

const PageHeader = styled.div`
  background: ${({ theme }) => theme.gradients.darkBlue};
  padding: 3rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.metallic.gold};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;
  justify-content: center;
`;

const CategoryButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ $active, theme }) => 
    $active ? theme.gradients.gold : theme.colors.background.card};
  color: ${({ $active, theme }) => 
    $active ? theme.colors.primary : theme.colors.text.dark};
  border: 2px solid ${({ $active, theme }) => 
    $active ? theme.colors.metallic.gold : theme.colors.border};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
    border-color: ${({ theme }) => theme.colors.metallic.gold};
  }
`;

const FeaturedPost = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  margin-bottom: 3rem;
  border: 2px solid ${({ theme }) => theme.colors.metallic.gold};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedImage = styled.div`
  background-size: cover;
  background-position: center;
  min-height: 400px;
  position: relative;
  background-color: #e9ecef;
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background: ${({ theme }) => theme.gradients.gold};
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: ${({ theme }) => theme.shadows.gold};
`;

const FeaturedContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FeaturedTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
  line-height: 1.3;
`;

const FeaturedExcerpt = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const PostCard = styled.div`
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all ${({ theme }) => theme.transitions.medium};
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
    border-color: ${({ theme }) => theme.colors.metallic.gold};
  }
`;

const PostImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
  background-color: #e9ecef;
  transition: transform ${({ theme }) => theme.transitions.slow};
  
  ${PostCard}:hover & {
    transform: scale(1.1);
  }
`;

const PostContent = styled.div`
  padding: 1.5rem;
`;

const PostCategory = styled.div`
  display: inline-block;
  background: ${({ theme }) => theme.gradients.gold};
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
`;

const PostTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.text.dark};
  line-height: 1.4;
`;

const PostExcerpt = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 1rem;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.muted};
  
  svg {
    color: ${({ theme }) => theme.colors.metallic.gold};
  }
`;

const ReadMoreButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.gradients.gold};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
    
    svg {
      transform: translateX(4px);
    }
  }
  
  svg {
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.metallic.gold};
  font-weight: 600;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.metallic.goldLight};
    
    svg {
      transform: translateX(4px);
    }
  }
  
  svg {
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
`;

export default BlogPage;
